import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories, comics } from '@/mocks/comics';
import type { ComicItem } from '@/mocks/comics';

type SortType = 'latest' | 'popular' | 'rating';

export default function CategoryPage() {
  const { slug = 'all' } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<SortType>('latest');
  const [searchTerm, setSearchTerm] = useState('');

  const currentCategory = categories.find(c => c.slug === slug) || categories[0];

  useEffect(() => {
    const catName = currentCategory.name === '全部' ? '漫画分类浏览' : currentCategory.name;
    document.title = `${catName} - 奇妙漫画网`;
  }, [currentCategory]);

  const filteredComics = useMemo(() => {
    let result: ComicItem[] = [];

    if (slug === 'all') {
      result = [...comics];
    } else {
      result = comics.filter(c => c.categories.includes(slug));
    }

    if (searchTerm.trim()) {
      const term = searchTerm.trim().toLowerCase();
      result = result.filter(c =>
        c.title.toLowerCase().includes(term) ||
        c.author.toLowerCase().includes(term) ||
        c.tags.some(t => t.toLowerCase().includes(term))
      );
    }

    switch (sortBy) {
      case 'latest':
        result.sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime());
        break;
      case 'popular':
        result.sort((a, b) => b.views - a.views);
        break;
      case 'rating':
        result.sort((a, b) => b.rating - a.rating);
        break;
    }

    return result;
  }, [slug, sortBy, searchTerm]);

  const sortOptions: { key: SortType; label: string }[] = [
    { key: 'latest', label: '最新更新' },
    { key: 'popular', label: '最多阅读' },
    { key: 'rating', label: '最高评分' },
  ];

  const catName = currentCategory.name === '全部' ? '漫画分类浏览' : `${currentCategory.name}漫画`;

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: catName,
    description: `奇妙漫画网${catName}专区，提供丰富的${catName}资源，免费在线阅读，高清画质。`,
    url: `https://www.qimiaomanhua.com/category/${slug}`,
    mainEntity: {
      '@type': 'ItemList',
      numberOfItems: filteredComics.length,
      itemListElement: filteredComics.map((comic, idx) => ({
        '@type': 'ListItem',
        position: idx + 1,
        item: {
          '@type': 'ComicSeries',
          name: comic.title,
          url: `https://www.qimiaomanhua.com/article/${comic.id}`,
        },
      })),
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.qimiaomanhua.com/' },
      { '@type': 'ListItem', position: 2, name: currentCategory.name, item: `https://www.qimiaomanhua.com/category/${slug}` },
    ],
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(categorySchema)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      <ScrollToTop />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          {/* Header */}
          <section className="bg-background-100 border-b border-background-200">
            <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-8">
              <Breadcrumb items={[
                { label: '首页', path: '/' },
                { label: currentCategory.name },
              ]} />
              <div className="flex items-center gap-3 mt-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                  <i className={`${currentCategory.icon} text-2xl`}></i>
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">{currentCategory.name}漫画</h1>
                  <p className="text-sm text-foreground-500 mt-0.5">共 {filteredComics.length} 部作品</p>
                </div>
              </div>
            </div>
          </section>

          {/* Category Tabs */}
          <section className="border-b border-background-200 bg-background-50 sticky top-16 z-30">
            <div className="max-w-[1280px] mx-auto px-4 md:px-6">
              <div className="flex items-center gap-1 overflow-x-auto py-2 scrollbar-hide flex-wrap">
                {categories.map(cat => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      slug === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-200'
                    }`}
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={`${cat.icon} text-xs`}></i>
                    </div>
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Toolbar */}
          <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                {sortOptions.map(opt => (
                  <button
                    key={opt.key}
                    onClick={() => setSortBy(opt.key)}
                    className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      sortBy === opt.key
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-200 text-foreground-600 hover:bg-background-300'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className="relative w-full sm:w-56">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  placeholder="搜索漫画名称或作者..."
                  className="w-full pl-9 pr-4 py-2 text-sm rounded-full border border-background-300 bg-background-100 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all duration-200"
                />
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
              </div>
            </div>
          </section>

          {/* Comic Grid */}
          <section className="max-w-[1280px] mx-auto px-4 md:px-6 pb-14">
            {filteredComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-5">
                {filteredComics.map(comic => (
                  <ComicCard key={comic.id} comic={comic} size="medium" />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200 text-foreground-400">
                  <i className="ri-search-line text-2xl"></i>
                </div>
                <p className="text-foreground-500 text-sm">没有找到相关漫画，试试其他关键词吧</p>
              </div>
            )}
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}